import React, { useEffect, useState } from 'react';
import { Modal, Button, Dropdown } from 'react-bootstrap';
import { Mesa, Objeto, Salon } from '../../../../../types';
import { FaTrash, FaSync } from 'react-icons/fa';
import { FaArrowRightArrowLeft, FaRegClone } from 'react-icons/fa6';
import { updateMesa, deleteMesa, changeMesaState, toggleVirtualByMesaId } from '../../../../../../../../crud/management_api/mesas';
import { updateObjeto, deleteObjeto } from '../../../../../../../../crud/management_api/objectos';
import { toast } from 'react-toastify';
import ChangeSalon from './components/ChangeSalon/ChangeSalon';
import { DuplicarMesa } from '../../../utils';
import './ItemControllerMobile.scss';
import { X } from 'react-bootstrap-icons';

interface ItemControllerMobileProps {
  show: boolean;
  handleClose: () => void;
  salones: Salon[];
  setSalones: (salones: Salon[]) => void;
  selectedItem: Mesa | Objeto | null;
  selectedSalon: Salon;
  setSelectedSalon: (salon: Salon) => void;
  setSelectedItem: (item: Mesa | Objeto | null) => void;
  updateSalon: (salon: Salon) => void;
  updateSelectedItem: (item: Mesa | Objeto) => void;
}

const ItemControllerMobile: React.FC<ItemControllerMobileProps> = ({
  show,
  handleClose,
  salones,
  setSalones,
  selectedItem,
  selectedSalon,
  setSelectedSalon,
  setSelectedItem,
  updateSalon,
  updateSelectedItem
}) => {
  const [people, setPeople] = useState(selectedItem?.people || 0);
  const [tag, setTag] = useState('');
  const [status, setStatus] = useState(selectedItem?.state || false);
  const [mesaNumber, setMesaNumber] = useState(selectedItem && 'num' in selectedItem ? selectedItem.num : '');
  const [objetoName, setObjetoName] = useState(selectedItem && !('num' in selectedItem) ? selectedItem.name : '');
  const [showMoveMesaModal, setShowMoveMesaModal] = useState(false);
  const [isVirtual, setIsVirtual] = useState(false);

  // Handler cuando ChangeSalon completa el movimiento
  const handleMoveComplete = (updatedSalones?: any[]) => {
    setShowMoveMesaModal(false);
    if (updatedSalones) {
      setSalones(updatedSalones);
      const newSel = updatedSalones.find((s: any) => s.salon_id === selectedSalon?.salon_id);
      if (newSel) setSelectedSalon(newSel);
    }
    // Limpiar selección y cerrar el modal principal
    setSelectedItem(null);
    handleClose();
  };

  useEffect(() => {
    if (selectedItem) {
      setPeople(selectedItem.people || 0);
      setStatus(selectedItem.state || false);
      setHeightInput(selectedItem.height?.toString() || '');
      setWidthInput(selectedItem.width?.toString() || '');
      // Only mesas have tags, objetos don't
      if ('num' in selectedItem) {
        setTag(selectedItem.tag || '');
        setMesaNumber(selectedItem.num);
        setIsVirtual(selectedItem.is_virtual || false);
      } else {
        setTag(''); // Objects don't have tags
        setObjetoName(selectedItem.name);
        setIsVirtual(false);
      }
    }
  }, [selectedItem]);

  // Helper to get current tag max length based on selectedItem dimensions (intervals)
  const getTagMaxLen = (item: Mesa | Objeto | null) => {
    if (!item) return 6;
    if (!('num' in item)) return 6;
    const w = item.width;
    const h = item.height;
    // intervals: <=70 ->2, <=80 ->3, <=100 ->4, >=110 ->6, default 4
    if (w <= 70 || h <= 70) return 2;
    if (w <= 80 || h <= 80) return 3;
    if (w <= 100 || h <= 100) return 4;
    if (w >= 110 || h >= 110) return 6;
    return 4;
  };

  // Expose variables/placeholders for tag limits so it's easy to extend rules later
  const tagMaxLen = getTagMaxLen(selectedItem);
  const tagPlaceholder = `Ingrese el tag (máx ${tagMaxLen} caracteres)`;
  
  const isMesa = !!(selectedItem && 'num' in selectedItem);

  const [showDuplicateTagWarning, setShowDuplicateTagWarning] = useState(false);
  const [showDuplicateMesaNumberWarning, setShowDuplicateMesaNumberWarning] = useState(false);
  
  // Local state for dimension inputs to allow free typing
  const [heightInput, setHeightInput] = useState(selectedItem?.height?.toString() || '');
  const [widthInput, setWidthInput] = useState(selectedItem?.width?.toString() || '');

  // Helper function to check if tag is duplicate across all mesas in all salones
  const isTagDuplicate = (tagValue: string, currentMesaId: number) => {
    if (!tagValue || tagValue.trim() === '') return false;
    
    const normalizedTag = tagValue.trim().toLowerCase();
    
    // Check all mesas in all salones
    for (const salon of salones) {
      for (const mesa of salon.mesas) {
        // Skip the current mesa being edited
        if (mesa.id === currentMesaId) continue;
        
        const mesaTag = (mesa.tag || '').toString().trim().toLowerCase();
        if (mesaTag === normalizedTag) {
          return true;
        }
      }
    }
    return false;
  };

  // Helper function to check if mesa number is duplicate across all mesas in all salones
  const isMesaNumberDuplicate = (mesaNumberValue: string, currentMesaId: number) => {
    if (!mesaNumberValue || mesaNumberValue.trim() === '') return false;
    
    const normalizedMesaNumber = mesaNumberValue.trim();
    
    // Check all mesas in all salones
    for (const salon of salones) {
      for (const mesa of salon.mesas) {
        // Skip the current mesa being edited
        if (mesa.id === currentMesaId) continue;
        
        const mesaNum = (mesa.num || '').toString().trim();
        if (mesaNum === normalizedMesaNumber) {
          return true;
        }
      }
    }
    return false;
  };

  // Truncate tag when dimensions change or selectedItem changes
  useEffect(() => {
    if (selectedItem && 'num' in selectedItem) {
      const maxLen = getTagMaxLen(selectedItem);
      if (tag && tag.length > maxLen) {
        setTag(tag.slice(0, maxLen));
      }
    }
  }, [selectedItem?.width, selectedItem?.height, selectedItem, tag]);

  const updateDimensions = (dimension: 'height' | 'width', value: number) => {
    if (!selectedItem || !selectedSalon) return;
    let updatedSalon = selectedSalon;
    if ('num' in selectedItem) {
      const updatedMesas = selectedSalon.mesas.map(mesa =>
        mesa.id === selectedItem.id ? { ...mesa, [dimension]: value } : mesa
      );
      updatedSalon = { ...selectedSalon, mesas: updatedMesas };
    } else {
      const updatedObjetos = selectedSalon.objetos.map(objeto =>
        objeto.id === selectedItem.id ? { ...objeto, [dimension]: value } : objeto
      );
      updatedSalon = { ...selectedSalon, objetos: updatedObjetos };
    }
    updateSalon(updatedSalon);
    updateSelectedItem({ ...selectedItem, [dimension]: value });
  };

  const handleSubmit = async () => {
    if (!selectedItem) return;
    
    // Validar tag duplicado antes de guardar (solo para mesas)
    if ('num' in selectedItem && tag && tag.trim()) {
      if (isTagDuplicate(tag, selectedItem.id)) {
        toast.error('No se puede guardar: El tag ya existe en otra mesa');
        setShowDuplicateTagWarning(true);
        return;
      }
    }
    
    // Validar número de mesa duplicado antes de guardar (solo para mesas)
    if ('num' in selectedItem && mesaNumber && mesaNumber.trim()) {
      if (isMesaNumberDuplicate(mesaNumber, selectedItem.id)) {
        toast.error('No se puede guardar: El número de mesa ya existe');
        setShowDuplicateMesaNumberWarning(true);
        return;
      }
    }
    
    if ('num' in selectedItem) {
      const mesaData = {
        mesa_number: mesaNumber,
        x_position: selectedItem.x_pos,
        y_position: selectedItem.y_pos,
        width: selectedItem.width,
        height: selectedItem.height,
        people: people,
        state: status,
        tag: (tag || '').toString().trim().slice(0, tagMaxLen) || null,
      };
      updateMesa(selectedItem.id, mesaData).then(() => {
        toast.success('Mesa actualizada con éxito');
        // Actualizar el estado global de salones y selectedSalon con la mesa actualizada
        const updatedMesas = selectedSalon.mesas.map(mesa =>
          mesa.id === selectedItem.id ? { ...mesa, ...mesaData } : mesa
        );
        const updatedSalon = { ...selectedSalon, mesas: updatedMesas };
        setSalones(
          salones.map(salon =>
            salon.salon_id === updatedSalon.salon_id ? updatedSalon : salon
          )
        );
        setSelectedSalon(updatedSalon);
        setSelectedItem(null);
        handleClose();
      }).catch((error) => {
        toast.error('Error al actualizar mesa');
        console.error('Error updating mesa:', error);
      });
    } else {
      // Usar el valor más reciente de objetoName
      const objetoData = {
        objeto_name: objetoName,
        x_position: selectedItem.x_pos,
        y_position: selectedItem.y_pos,
        width: selectedItem.width,
        height: selectedItem.height,
        state: status,
        // Objects don't have tags
      };
      updateObjeto(selectedItem.id, objetoData).then(() => {
        toast.success('Objeto actualizado con éxito');
        // Actualizar el estado global de salones y selectedSalon con el objeto actualizado
        const updatedObjetos = selectedSalon.objetos.map(objeto =>
          objeto.id === selectedItem.id ? { ...objeto, ...objetoData, name: objetoName } : objeto
        );
        const updatedSalon = { ...selectedSalon, objetos: updatedObjetos };
        setSalones(
          salones.map(salon =>
            salon.salon_id === updatedSalon.salon_id ? updatedSalon : salon
          )
        );
        setSelectedSalon(updatedSalon);
        setSelectedItem({ ...selectedItem, name: objetoName });
        setTimeout(() => setSelectedItem(null), 300); // Cierra el modal tras breve delay
        handleClose();
      }).catch((error) => {
        toast.error('Error al actualizar objeto');
        console.error('Error updating objeto:', error);
      });
    }
  };

  const handleDeleteMesa = () => {
    if (!selectedItem || !('num' in selectedItem)) return;
    deleteMesa(selectedItem.id).then(() => {
      toast.success('Mesa eliminada con éxito');
      const updatedMesas: Mesa[] = selectedSalon.mesas.filter(mesa => mesa.id !== selectedItem.id);
      const updatedSalon = { ...selectedSalon, mesas: updatedMesas };
      setSalones(
        salones.map(salon =>
          salon.salon_id === updatedSalon.salon_id ? updatedSalon : salon
        )
      );
      setSelectedSalon(updatedSalon);
      setSelectedItem(null);
      handleClose();
    }).catch((error) => {
      toast.error('Error al eliminar la mesa');
      console.error('Error deleting mesa:', error.response.data.message);
    });
  };

  const handleDeleteObjeto = () => {
    if (!selectedItem || 'num' in selectedItem) return;
    deleteObjeto(selectedItem.id).then(() => {
      toast.success('Objeto eliminado con éxito');
      const updatedObjetos: Objeto[] = selectedSalon.objetos.filter(objeto => objeto.id !== selectedItem.id);
      const updatedSalon = { ...selectedSalon, objetos: updatedObjetos };
      setSalones(
        salones.map(salon =>
          salon.salon_id === updatedSalon.salon_id ? updatedSalon : salon
        )
      );
      setSelectedSalon(updatedSalon);
      setSelectedItem(null);
      handleClose();
    }).catch((error) => {
      toast.error('Error al eliminar el objeto');
      console.error('Error deleting objeto:', error.response.data.message);
    });
  };

  const handleVirtualToggle = async () => {
    if (!selectedItem || !('num' in selectedItem)) return;

    const newVirtualState = !isVirtual;
    setIsVirtual(newVirtualState);

    try {
      await toggleVirtualByMesaId(selectedItem.id);

      // Update local state
      const updatedMesas = selectedSalon.mesas.map(m =>
        m.id === selectedItem.id ? { ...m, is_virtual: newVirtualState } : m
      );
      const updatedSalon = { ...selectedSalon, mesas: updatedMesas };
      setSalones(
        salones.map(salon =>
          salon.salon_id === updatedSalon.salon_id ? updatedSalon : salon
        )
      );
      setSelectedSalon(updatedSalon);
      setSelectedItem({ ...selectedItem, is_virtual: newVirtualState });

      toast.success(newVirtualState ? 'Mesa configurada como virtual' : 'Mesa ya no es virtual');
    } catch (error) {
      // Revert on error
      setIsVirtual(!newVirtualState);
      toast.error('Error actualizando estado virtual de la mesa');
      console.error('Error toggling virtual state:', error);
    }
  };

  const handleMesaNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedItem || !('num' in selectedItem) || !selectedSalon) return;
    const newMesaNumber = e.target.value;
    setMesaNumber(newMesaNumber);
    
    // Check for duplicate mesa number
    if (newMesaNumber && newMesaNumber.trim()) {
      const isDuplicate = isMesaNumberDuplicate(newMesaNumber, selectedItem.id);
      setShowDuplicateMesaNumberWarning(isDuplicate);
    } else {
      setShowDuplicateMesaNumberWarning(false);
    }
    
    const updatedSelectedItem = { ...selectedItem, num: newMesaNumber };
    setSelectedItem(updatedSelectedItem);
    const updatedMesas = selectedSalon.mesas.map(mesa =>
      mesa.id === selectedItem.id ? { ...mesa, num: newMesaNumber } : mesa
    );
    updateSalon({ ...selectedSalon, mesas: updatedMesas });
  };

  const handleObjetoNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedItem || 'num' in selectedItem || !selectedSalon) return;
    const newObjetoName = e.target.value;
    setObjetoName(newObjetoName);
    const updatedSelectedItem = { ...selectedItem, name: newObjetoName };
    setSelectedItem(updatedSelectedItem);
    const updatedObjetos = selectedSalon.objetos.map(objeto =>
      objeto.id === selectedItem.id ? { ...objeto, name: newObjetoName } : objeto
    );
    updateSalon({ ...selectedSalon, objetos: updatedObjetos });
  };

  const handleStatusChange = () => {
    const newStatus = !status;
    setStatus(newStatus);
    if (!selectedItem || !selectedSalon) return;
    if ('num' in selectedItem) {
      changeMesaState(selectedItem.id)
        .then(() => {
          const updatedMesas = selectedSalon.mesas.map((mesa) =>
            mesa.id === selectedItem.id ? { ...mesa, state: newStatus } : mesa
          );
          const updatedSalon = { ...selectedSalon, mesas: updatedMesas };
          setSalones(
            salones.map((salon) =>
              salon.salon_id === updatedSalon.salon_id ? updatedSalon : salon
            )
          );
          setSelectedSalon(updatedSalon);
          setSelectedItem({ ...selectedItem, state: newStatus });
        })
        .catch((error) => {
          toast.error('Error al cambiar el estado de la mesa');
          setStatus(!newStatus);
          console.error('Error changing mesa state:', error?.response?.data?.message);
        });
    } else {
      const updatedObjetos = selectedSalon.objetos.map((objeto) =>
        objeto.id === selectedItem.id ? { ...objeto, state: newStatus } : objeto
      );
      const updatedSalon = { ...selectedSalon, objetos: updatedObjetos };
      setSalones(
        salones.map((salon) =>
          salon.salon_id === updatedSalon.salon_id ? updatedSalon : salon
        )
      );
      setSelectedSalon(updatedSalon);
      setSelectedItem({ ...selectedItem, state: newStatus });
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="item-controller-mobile-modal">
      <Modal.Header className="modal-header-custom">
        <div className="form-modal-title-container">
        <Modal.Title className="modal-title-custom">
          {selectedItem && (
            <>
              {isMesa
                ? `Mesa ${selectedItem?.num}` 
                : selectedItem?.name || 'Objeto'
              }
              <span className="modal-subtitle">
                {isMesa 
                  ? `${people} ${people <= 1 ? 'Persona' : 'Personas'}` 
                  : 'Configurar elemento'
                }
              </span>
            </>
          )}
        </Modal.Title>
                </div>
                            <Button variant="none" className="modal-close-button" onClick={handleClose}>
                                <X size={24} />
                            </Button>

      </Modal.Header>
      <Modal.Body className="modal-body-custom">
        {selectedItem && (
          <div className="panel-content-mobile">
            {/* Status toggle and action buttons */}
            <div className="status-and-actions">
              <div className="status-toggle-mobile">
                <div className="status-section">
                  <span className="status-label">
                    {status ? 'Activo' : 'Inactivo'}
                  </span>
                  <label className="modern-switch">
                    <input
                      type="checkbox"
                      checked={status}
                      onChange={handleStatusChange}
                    />
                    <span className="switch-slider"></span>
                  </label>
                </div>
                
                <div className="action-buttons-mobile">
                  {isMesa && (
                    <>
                      <Button
                        className="action-btn-mobile duplicate-btn"
                        variant='none'
                        onClick={() => DuplicarMesa(
                          selectedItem,
                          selectedSalon,
                          setSelectedItem,
                          (salon) => {
                            updateSalon(salon);
                            setSalones(
                              salones.map(s =>
                                s.salon_id === salon.salon_id ? salon : s
                              )
                            );
                          },
                          setSalones,
                          salones
                        )}
                        title="Duplicar mesa"
                      >
                        <FaRegClone />
                      </Button>
                      <Button 
                        className="action-btn-mobile move-btn" 
                        variant='none' 
                        onClick={() => setShowMoveMesaModal(true)}
                        title="Mover a otro salón"
                      >
                        <FaArrowRightArrowLeft />
                      </Button>
                    </>
                  )}
                  <Button 
                    className="action-btn-mobile delete-btn" 
                    variant='none' 
                    onClick={isMesa ? handleDeleteMesa : handleDeleteObjeto}
                    title={`Eliminar ${isMesa ? 'mesa' : 'objeto'}`}
                  >
                    <FaTrash />
                  </Button>
                </div>
              </div>
            </div>

            {/* Configuration form */}
            <div className="config-form-mobile">
              {isMesa ? (
                <>
                  <div className="form-group-mobile">
                    <label className="form-label-mobile">Número de Mesa</label>
                    <input
                      type="number"
                      value={mesaNumber}
                      onChange={handleMesaNumberChange}
                      placeholder="Número de Mesa"
                      className="form-input-mobile"
                    />
                    {showDuplicateMesaNumberWarning && (
                      <div className="tag-warning-banner-mobile">
                        ⚠️ Ya existe una mesa con este número
                      </div>
                    )}
                  </div>
                  
                  <div className="form-group-mobile">
                    <label className="form-label-mobile">Capacidad</label>
                    <Dropdown className='capacity-dropdown-mobile'>
                      <Dropdown.Toggle variant="none" className='capacity-toggle-mobile'>
                        {people} {people <= 1 ? 'Persona' : 'Personas'}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {Array.from({ length: 15 }, (_, i) => i + 1).map(number => (
                          <Dropdown.Item key={number} onClick={() => setPeople(number)}>
                            {number} {number <= 1 ? 'Persona' : 'Personas'}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  <div className="form-group-mobile">
                    <label className="form-label-mobile">Tag</label>
                    <input
                      type="text"
                      value={tag}
                      onChange={(e) => {
                        const newTag = e.target.value.slice(0, tagMaxLen);
                        setTag(newTag);
                        
                        // Check for duplicate tag
                        if (selectedItem && 'num' in selectedItem) {
                          const isDupe = isTagDuplicate(newTag, selectedItem.id);
                          setShowDuplicateTagWarning(isDupe);
                        }
                      }}
                      placeholder={tagPlaceholder}
                      className="form-input-mobile"
                      maxLength={tagMaxLen}
                    />
                    {showDuplicateTagWarning && (
                      <div className="tag-warning-banner-mobile">
                        ⚠️ Ya existe una mesa con este tag
                      </div>
                    )}
                  </div>

                  <div className="form-group-mobile">
                    <label className="form-label-mobile">Mesa Virtual (Caja)</label>
                    <div className="virtual-toggle-container-mobile">
                      <span className="virtual-label-mobile">
                        {isVirtual ? 'Mesa Virtual para Delivery/Recoger' : 'Mesa Normal'}
                      </span>
                      <label className="modern-switch">
                        <input
                          type="checkbox"
                          checked={isVirtual}
                          onChange={handleVirtualToggle}
                        />
                        <span className="switch-slider"></span>
                      </label>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group-mobile">
                    <label className="form-label-mobile">Nombre del Objeto</label>
                    <input
                      type="text"
                      value={objetoName}
                      onChange={handleObjetoNameChange}
                      placeholder="Ingrese el nombre"
                      className="form-input-mobile"
                    />
                  </div>
                </>
              )}

              {/* Dimension controls */}
              <div className="dimension-controls-mobile">
                <div className="dimension-group-mobile">
                  <div className="dimension-header-mobile">
                    <label className="dimension-label-mobile">Alto</label>
                    <input
                      type="number"
                      min="10"
                      max="500"
                      value={heightInput}
                      onChange={(e) => {
                        setHeightInput(e.target.value);
                        const val = Number(e.target.value);
                        if (val >= 10 && val <= 500) {
                          updateDimensions('height', val);
                        }
                      }}
                      onBlur={(e) => {
                        const val = Number(e.target.value);
                        if (val < 10 || val > 500 || !e.target.value) {
                          setHeightInput(selectedItem.height.toString());
                        }
                      }}
                      className="dimension-input-mobile"
                      placeholder="px"
                    />
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="500"
                    value={selectedItem.height}
                    onChange={(e) => updateDimensions('height', Number(e.target.value))}
                    className="dimension-slider-mobile"
                  />
                </div>
                <div className="dimension-group-mobile">
                  <div className="dimension-header-mobile">
                    <label className="dimension-label-mobile">Ancho</label>
                    <input
                      type="number"
                      min="10"
                      max="500"
                      value={widthInput}
                      onChange={(e) => {
                        setWidthInput(e.target.value);
                        const val = Number(e.target.value);
                        if (val >= 10 && val <= 500) {
                          updateDimensions('width', val);
                        }
                      }}
                      onBlur={(e) => {
                        const val = Number(e.target.value);
                        if (val < 10 || val > 500 || !e.target.value) {
                          setWidthInput(selectedItem.width.toString());
                        }
                      }}
                      className="dimension-input-mobile"
                      placeholder="px"
                    />
                  </div>
                  <input
                    type="range"
                    min="10"
                    max="500"
                    value={selectedItem.width}
                    onChange={(e) => updateDimensions('width', Number(e.target.value))}
                    className="dimension-slider-mobile"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <ChangeSalon
          show={showMoveMesaModal}
          setShow={setShowMoveMesaModal}
          selectedSalon={selectedSalon}
          setSelectedSalon={setSelectedSalon}
          mesaId={selectedItem?.id || 0}
          salones={salones}
          setSalones={setSalones}
          onMoveComplete={handleMoveComplete}
        />
      </Modal.Body>
      <Modal.Footer className='modal-footer-custom'>
        <Button variant="none" className='footer-btn-mobile cancel-btn' onClick={handleClose}>
          Cancelar
        </Button>
        <Button 
          variant="none" 
          className='footer-btn-mobile save-btn' 
          onClick={handleSubmit}
          disabled={showDuplicateTagWarning || showDuplicateMesaNumberWarning}
        >
          <FaSync className="btn-icon" />
          {isMesa ? 'Guardar' : 'Actualizar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ItemControllerMobile;